import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { toast } from 'sonner';
import { API_URL } from '../components/admin/AdminShared';
import {
  Users, Home, CreditCard, FileCheck, Eye, Lock, Settings,
  Briefcase, PenLine, TrendingUp, BarChart3, KeyRound, Megaphone,
  Code, ShieldCheck, Calculator, LineChart, Zap, BadgeCheck, Newspaper, Radar, Wallet, Compass, Send, Rocket, Target
} from 'lucide-react';
import DashboardTabs from '../components/DashboardTabs';
import { useQueryTab } from '../hooks/useQueryTab';

// Tab Components
import { InvitationMessages, AdminStats, UsersTab, PropertiesTab, TransactionsTab, VerificationsTab, GDPRTab } from '../components/admin/AdminDataTabs';
import { FilesTab, ReferralsTab } from '../components/admin/AdminFilesReferrals';
import { SettingsTab } from '../components/admin/AdminSettings';
import { CalculatorsTab } from '../components/admin/AdminCalculators';
import { LandingContentTab } from '../components/admin/AdminLandingContent';
import { AdsTab } from '../components/admin/AdminAds';
import { RevenueTab } from '../components/admin/AdminRevenue';
import { AnalyticsTab } from '../components/admin/AdminAnalytics';
import { EmbedRequestsTab } from '../components/admin/AdminEmbedRequests';
import { MyAccountTab } from '../components/admin/AdminMyAccount';
import { AgentVerificationsTab } from '../components/admin/AdminAgentVerifications';
import { AIAccountantTab } from '../components/admin/AdminAIAccountant';
import { AISalesManagerTab } from '../components/admin/AdminAISalesManager';
import { AdminViralTab } from '../components/admin/AdminViralTab';
import { AdminSalesTab } from '../components/admin/AdminSalesTab';
import { AdminProfessionalOnboardingTab } from '../components/admin/AdminProfessionalOnboarding';
import { AdminAMLTab } from '../components/admin/AdminAMLTab';
import { AdminPressOutreach } from '../components/admin/AdminPressOutreach';
import { AdminSovereignOS } from '../components/admin/AdminSovereignOS';
import { AdminStripeBalance } from '../components/admin/AdminStripeBalance';
import { AdminNorthIndia } from '../components/admin/AdminNorthIndia';
import AdminAutomatedConnections from '../components/admin/AdminAutomatedConnections';
import { AdminSovereignDashboard } from '../components/admin/AdminSovereignDashboard';
import { AdminCostMonitor } from '../components/admin/AdminCostMonitor';
import { AdminFBCalendar } from '../components/admin/AdminFBCalendar';
import { AdminFBAutoPost } from '../components/admin/AdminFBAutoPost';
import { AdminSGA } from '../components/admin/AdminSGA';
import AdminVisionTargets from '../components/admin/AdminVisionTargets';
import AdminHealthStrip from '../components/admin/AdminHealthStrip';
import AdminLiveConversionTile from '../components/admin/AdminLiveConversionTile';
import AdminMetaHealthTile from '../components/admin/AdminMetaHealthTile';
import AdminOnboardingChecklist from '../components/admin/AdminOnboardingChecklist';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useQueryTab('overview', ['overview','analytics','revenue','sovereign-dashboard','ads','users','properties','transactions','verifications','gdpr','referrals','viral','professionals','aml','agent-verify','landing','settings','my-account','ai-accountant','ai-sales','embed-requests','calculators','press-outreach','sovereign','stripe-balance','north-india','automated-connections','cost-monitor','fb-calendar','fb-autopost','sga','vision']);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try { setStats((await axios.get(`${API_URL}/api/admin/stats`)).data); }
    catch { toast.error('Failed to load stats'); }
    finally { setLoading(false); }
  }, []);
  const fetchUsers = useCallback(async () => {
    try { setUsers((await axios.get(`${API_URL}/api/admin/users`)).data); } catch { toast.error('Failed to load users'); }
  }, []);
  const fetchProperties = useCallback(async () => {
    try { setProperties((await axios.get(`${API_URL}/api/admin/properties`)).data); } catch { toast.error('Failed to load properties'); }
  }, []);
  const fetchTransactions = useCallback(async () => {
    try { setTransactions((await axios.get(`${API_URL}/api/admin/transactions`)).data); } catch { toast.error('Failed to load transactions'); }
  }, []);
  const fetchVerifications = useCallback(async () => {
    try { setVerifications((await axios.get(`${API_URL}/api/admin/verifications`)).data); } catch { toast.error('Failed to load verifications'); }
  }, []);
  const fetchContactRequests = useCallback(async () => {
    try { setContactRequests((await axios.get(`${API_URL}/api/admin/contact-requests`)).data); } catch { toast.error('Failed to load GDPR requests'); }
  }, []);
  const fetchReferrals = useCallback(async () => {
    try { setReferrals((await axios.get(`${API_URL}/api/referrals`)).data); } catch { toast.error('Failed to load referrals'); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'properties') fetchProperties();
    if (activeTab === 'transactions') fetchTransactions();
    if (activeTab === 'verifications') fetchVerifications();
    if (activeTab === 'gdpr') fetchContactRequests();
    if (activeTab === 'referrals') fetchReferrals();
  }, [activeTab, fetchUsers, fetchProperties, fetchTransactions, fetchVerifications, fetchContactRequests, fetchReferrals]);

  const handleVerification = async (userId, status) => {
    try {
      const action = status === 'verified' ? 'approve' : 'reject';
      await axios.put(`${API_URL}/api/admin/verifications/${userId}/${action}`, {});
      toast.success(`Verification ${status}`);
      fetchVerifications(); fetchUsers(); fetchStats();
    } catch { toast.error('Failed to update verification'); }
  };

  const handleContactRequest = async (requestId, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/contact-requests/${requestId}?status=${status}`, {});
      toast.success(`Contact request ${status}`);
      fetchContactRequests(); fetchStats();
    } catch { toast.error('Failed to update contact request'); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}" and all their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`);
      toast.success('User deleted'); fetchUsers(); fetchStats();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete user'); }
  };

  const handleRoleChange = async (userId, userName, newRole) => {
    if (!window.confirm(`Change ${userName}'s role to "${newRole}"? They will see a different dashboard on next login.`)) {
      fetchUsers(); return;
    }
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role`, { role: newRole });
      toast.success(`${userName}'s role changed to ${newRole}`); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to change role'); fetchUsers(); }
  };

  const handleDeleteProperty = async (propertyId, title) => {
    if (!window.confirm(`Delete property "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/properties/${propertyId}`);
      toast.success('Property deleted'); fetchProperties(); fetchStats();
    } catch { toast.error('Failed to delete property'); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', shortLabel: 'Overview', icon: <Eye className="h-4 w-4" /> },
    { id: 'analytics', label: 'Analytics', shortLabel: 'Analytics', icon: <TrendingUp className="h-4 w-4" /> },
    { id: 'revenue', label: 'Revenue', shortLabel: 'Revenue', icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'sovereign-dashboard', label: 'Sovereign Dashboard', shortLabel: 'Sovereign $', icon: <Wallet className="h-4 w-4" /> },
    { id: 'cost-monitor', label: 'Cost Monitor', shortLabel: 'Credits', icon: <Zap className="h-4 w-4" /> },
    { id: 'fb-calendar', label: 'FB Calendar', shortLabel: 'FB Cal', icon: <Newspaper className="h-4 w-4" /> },
    { id: 'fb-autopost', label: 'FB Auto-Post', shortLabel: 'FB Auto', icon: <Send className="h-4 w-4" /> },
    { id: 'sga', label: 'Growth Agent', shortLabel: 'SGA', icon: <Rocket className="h-4 w-4" /> },
    { id: 'ads', label: 'Adverts & Partners', shortLabel: 'Adverts', icon: <Megaphone className="h-4 w-4" /> },
    { id: 'users', label: 'Users', shortLabel: 'Users', icon: <Users className="h-4 w-4" /> },
    { id: 'properties', label: 'Properties', shortLabel: 'Props', icon: <Home className="h-4 w-4" /> },
    { id: 'transactions', label: 'Payments', shortLabel: 'Payments', icon: <CreditCard className="h-4 w-4" /> },
    { id: 'verifications', label: 'ID Checks', shortLabel: 'ID', icon: <FileCheck className="h-4 w-4" />, badge: stats?.pending_verifications || 0 },
    { id: 'gdpr', label: 'Data Requests', shortLabel: 'GDPR', icon: <Lock className="h-4 w-4" />, badge: stats?.pending_contact_requests || 0 },
    { id: 'referrals', label: 'Referrals', shortLabel: 'Referrals', icon: <Briefcase className="h-4 w-4" /> },
    { id: 'viral', label: 'Viral Network', shortLabel: 'Viral', icon: <TrendingUp className="h-4 w-4" /> },
    { id: 'sales', label: 'Sales Funnel', shortLabel: 'Sales', icon: <Zap className="h-4 w-4" /> },
    { id: 'agent-verify', label: 'Agent Checks', shortLabel: 'Agents', icon: <ShieldCheck className="h-4 w-4" /> },
    { id: 'professionals', label: 'Pro KYP', shortLabel: 'Pro KYP', icon: <BadgeCheck className="h-4 w-4" /> },
    { id: 'sovereign', label: 'Sovereign OS (IN)', shortLabel: 'Sovereign', icon: <Radar className="h-4 w-4" />, badge: stats?.pending_applications || 0 },
    { id: 'automated-connections', label: 'Automated Connections', shortLabel: 'Connections', icon: <Radar className="h-4 w-4" /> },
    { id: 'north-india', label: 'North India Hub', shortLabel: 'North IN', icon: <Compass className="h-4 w-4" /> },
    { id: 'stripe-balance', label: 'Stripe Treasury', shortLabel: 'Treasury', icon: <Wallet className="h-4 w-4" /> },
    { id: 'aml', label: 'AML Compliance', shortLabel: 'AML', icon: <ShieldCheck className="h-4 w-4" /> },
    { id: 'landing', label: 'Landing Page', shortLabel: 'Landing', icon: <PenLine className="h-4 w-4" /> },
    { id: 'press-outreach', label: 'Press Outreach', shortLabel: 'Press', icon: <Newspaper className="h-4 w-4" /> },
    { id: 'settings', label: 'Settings', shortLabel: 'Settings', icon: <Settings className="h-4 w-4" /> },
    { id: 'my-account', label: 'My Account', shortLabel: 'Account', icon: <KeyRound className="h-4 w-4" /> },
    { id: 'ai-accountant', label: 'AI Accountant', shortLabel: 'Accountant', icon: <Calculator className="h-4 w-4" /> },
    { id: 'ai-sales', label: 'AI Sales Manager', shortLabel: 'Sales AI', icon: <LineChart className="h-4 w-4" /> },
    { id: 'vision', label: 'Vision Targets', shortLabel: 'Vision', icon: <Target className="h-4 w-4" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0C] premium-theme" data-testid="admin-dashboard">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] text-[#D4AF37] mb-2 block uppercase font-bold">Administration</span>
          <h1 className="font-['Playfair_Display'] text-4xl text-white">Admin Dashboard</h1>
        </div>

        {/* Auto-healing health probes — only renders when there is something
            to alert about, otherwise shows a tiny "all green" pill. */}
        <AdminHealthStrip dataTestId="admin-health-strip" />

        {/* Live Conversion Pulse — money-flow dashboard, auto-refreshes 30s */}
        <AdminLiveConversionTile />

        {/* Meta WhatsApp gates — token, phone-number-id, approved templates */}
        <AdminMetaHealthTile />

        {/* Live onboarding checklist — every item is a real-time probe */}
        <AdminOnboardingChecklist dataTestId="admin-onboarding" />

        {/* Tabs */}
        <DashboardTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          tabs={tabs.map(t => ({ ...t, testId: `tab-${t.id}` }))}
        />

        {activeTab === 'overview' && stats && <AdminStats stats={stats} />}
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'revenue' && <RevenueTab />}
        {activeTab === 'sovereign-dashboard' && <AdminSovereignDashboard />}
        {activeTab === 'cost-monitor' && <AdminCostMonitor />}
        {activeTab === 'fb-calendar' && <AdminFBCalendar />}
        {activeTab === 'fb-autopost' && <AdminFBAutoPost />}
        {activeTab === 'sga' && <AdminSGA />}
        {activeTab === 'ads' && <AdsTab />}
        {activeTab === 'users' && <UsersTab users={users} onDelete={handleDeleteUser} onRoleChange={handleRoleChange} />}
        {activeTab === 'properties' && <PropertiesTab properties={properties} onDelete={handleDeleteProperty} />}
        {activeTab === 'transactions' && <TransactionsTab transactions={transactions} />}
        {activeTab === 'verifications' && <VerificationsTab verifications={verifications} onAction={handleVerification} />}
        {activeTab === 'gdpr' && <GDPRTab contactRequests={contactRequests} onAction={handleContactRequest} />}
        {activeTab === 'referrals' && <ReferralsTab referrals={referrals} onRefresh={fetchReferrals} />}
        {activeTab === 'viral' && <AdminViralTab />}
        {activeTab === 'sales' && <AdminSalesTab />}
        {activeTab === 'embed-requests' && <EmbedRequestsTab />}
        {activeTab === 'agent-verify' && <AgentVerificationsTab />}
        {activeTab === 'professionals' && <AdminProfessionalOnboardingTab />}
        {activeTab === 'aml' && <AdminAMLTab />}
        {activeTab === 'landing' && <LandingContentTab />}
        {activeTab === 'press-outreach' && <AdminPressOutreach />}
        {activeTab === 'sovereign' && <AdminSovereignOS />}
        {activeTab === 'automated-connections' && <AdminAutomatedConnections />}
        {activeTab === 'north-india' && <AdminNorthIndia />}
        {activeTab === 'stripe-balance' && <AdminStripeBalance />}
        {activeTab === 'calculators' && <CalculatorsTab />}
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'my-account' && <MyAccountTab />}
        {activeTab === 'ai-accountant' && <AIAccountantTab />}
        {activeTab === 'ai-sales' && <AISalesManagerTab />}
        {activeTab === 'vision' && <AdminVisionTargets />}
      </div>
    </div>
  );
};

export default AdminDashboard;
